import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let current = '';

      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.clientHeight;
        if (window.scrollY >= sectionTop - 200) {
          current = section.getAttribute('id');
        }
      });

      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const tableOfContents = [
    { id: 'introduction', title: 'Introduction' },
    { id: 'information-collect', title: 'Information We Collect' },
    { id: 'how-we-use', title: 'How We Use Your Information' },
    { id: 'information-sharing', title: 'Information Sharing' },
    { id: 'data-security', title: 'Data Security' },
    { id: 'your-rights', title: 'Your Rights' },
    { id: 'cookies', title: 'Cookies & Tracking' },
    { id: 'advertising', title: 'Advertising' },
    { id: 'third-party-links', title: 'Third-Party Links' },
    { id: 'children-privacy', title: 'Children\'s Privacy' },
    { id: 'policy-updates', title: 'Policy Updates' },
    { id: 'contact', title: 'Contact Us' }
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy - VestrioZ</title>
        <meta
          name="description"
          content="Read VestrioZ's privacy policy to understand how we collect, use, and protect your personal information."
        />
        <meta name="keywords" content="privacy policy, data protection, VestrioZ" />
      </Helmet>
      <div className="min-h-screen" style={{ backgroundColor: '#F6F1EB' }}>
        {/* Hero Section */}
        <div className="relative overflow-hidden" style={{ backgroundColor: '#222831' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent"></div>
          <div className="relative max-w-6xl mx-auto px-4 py-16">
            <Link
              to="/"
              className="inline-flex items-center text-white/80 hover:text-white transition-all duration-300 mb-8 group"
            >
              <i className="fas fa-arrow-left mr-2 group-hover:-translate-x-1 transition-transform"></i>
              Back to VestrioZ
            </Link>
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 animate-fade-in">
                Privacy Policy
              </h1>
              <p className="text-xl text-white/90 mb-4">
                Your privacy matters to us
              </p>
              <p className="text-white/70">
                Last updated: {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Table of Contents - Sticky Sidebar */}
            <div className="lg:w-1/4">
              <div className="sticky top-8">
                <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4" style={{ borderColor: '#948979' }}>
                  <h3 className="text-lg font-bold mb-4" style={{ color: '#222831' }}>
                    Table of Contents
                  </h3>
                  <nav className="space-y-2">
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 ${activeSection === item.id
                          ? 'text-white shadow-md'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                        style={activeSection === item.id ? { backgroundColor: '#948979' } : {}}
                      >
                        {item.title}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:w-3/4">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-8 lg:p-12 space-y-12">

                  {/* Introduction */}
                  <section id="introduction" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#948979' }}>
                        <i className="fas fa-shield-alt text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Introduction
                      </h2>
                    </div>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-l-4" style={{ borderColor: '#948979' }}>
                      <p className="text-gray-700 leading-relaxed text-lg">
                        Welcome to <span className="font-semibold" style={{ color: '#222831' }}>VestrioZ</span>. We respect your privacy and are committed to protecting your personal data.
                        This privacy policy explains how we collect, use, and safeguard your information when you use our
                        fashion and jewellery sharing platform.
                      </p>
                    </div>
                  </section>

                  {/* Information We Collect */}
                  <section id="information-collect" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#393E46' }}>
                        <i className="fas fa-database text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Information We Collect
                      </h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-white border rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                        <div className="flex items-center mb-4">
                          <i className="fas fa-user text-2xl mr-3" style={{ color: '#948979' }}></i>
                          <h3 className="text-xl font-semibold" style={{ color: '#393E46' }}>
                            Personal Information
                          </h3>
                        </div>
                        <ul className="space-y-3 text-gray-700">
                          <li className="flex items-start">
                            <i className="fas fa-check-circle text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Name and contact information (email, phone number)</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-check-circle text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Profile information and preferences</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-check-circle text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Location information for item listings</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-check-circle text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Payment information (processed securely through Razorpay)</span>
                          </li>
                        </ul>
                      </div>
                      <div className="bg-white border rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                        <div className="flex items-center mb-4">
                          <i className="fas fa-chart-line text-2xl mr-3" style={{ color: '#948979' }}></i>
                          <h3 className="text-xl font-semibold" style={{ color: '#393E46' }}>
                            Usage Information
                          </h3>
                        </div>
                        <ul className="space-y-3 text-gray-700">
                          <li className="flex items-start">
                            <i className="fas fa-check-circle text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Items you list, rent, or browse</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-check-circle text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Messages and communications on our platform</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-check-circle text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Usage patterns and preferences</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-check-circle text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Device information and IP address</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* How We Use Your Information */}
                  <section id="how-we-use" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#948979' }}>
                        <i className="fas fa-cogs text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        How We Use Your Information
                      </h2>
                    </div>
                    <div className="grid gap-4">
                      {[
                        { icon: 'fas fa-server', text: 'Provide and maintain our fashion sharing services' },
                        { icon: 'fas fa-credit-card', text: 'Process transactions and payments securely' },
                        { icon: 'fas fa-comments', text: 'Facilitate communication between users' },
                        { icon: 'fas fa-bell', text: 'Send important updates and notifications' },
                        { icon: 'fas fa-chart-bar', text: 'Improve our platform and user experience' },
                        { icon: 'fas fa-shield-alt', text: 'Prevent fraud and ensure platform security' },
                        { icon: 'fas fa-gavel', text: 'Comply with legal obligations' }
                      ].map((item, index) => (
                        <div key={index} className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <i className={`${item.icon} text-xl mr-4`} style={{ color: '#948979' }}></i>
                          <span className="text-gray-700">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* Information Sharing */}
                  <section id="information-sharing" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#393E46' }}>
                        <i className="fas fa-share-alt text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Information Sharing
                      </h2>
                    </div>
                    <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-xl mb-6">
                      <div className="flex items-center mb-2">
                        <i className="fas fa-exclamation-triangle text-red-500 mr-2"></i>
                        <p className="text-red-800 font-semibold">Important: We do not sell your personal information.</p>
                      </div>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-6">
                      We may share your information only in these specific circumstances:
                    </p>
                    <div className="space-y-4">
                      {[
                        { icon: 'fas fa-handshake', text: 'With other users as necessary for transactions (contact info for rentals)', color: 'blue' },
                        { icon: 'fas fa-credit-card', text: 'With payment processors (Razorpay) for secure transactions', color: 'green' },
                        { icon: 'fas fa-tools', text: 'With service providers who help us operate our platform', color: 'purple' },
                        { icon: 'fas fa-balance-scale', text: 'When required by law or to protect our rights', color: 'red' },
                        { icon: 'fas fa-check-circle', text: 'With your explicit consent', color: 'indigo' }
                      ].map((item, index) => (
                        <div key={index} className={`flex items-start p-4 bg-${item.color}-50 rounded-lg border-l-4 border-${item.color}-400`}>
                          <i className={`${item.icon} text-${item.color}-500 text-lg mr-4 mt-1`}></i>
                          <span className="text-gray-700">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* Data Security */}
                  <section id="data-security" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#948979' }}>
                        <i className="fas fa-lock text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Data Security
                      </h2>
                    </div>
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-xl">
                      <div className="flex items-start">
                        <i className="fas fa-shield-alt text-3xl text-green-500 mr-4 mt-1"></i>
                        <div>
                          <p className="text-gray-700 leading-relaxed mb-4">
                            We implement appropriate security measures to protect your personal information against unauthorized
                            access, alteration, disclosure, or destruction. This includes encryption, secure servers, and regular
                            security assessments.
                          </p>
                          <div className="bg-yellow-100 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                            <p className="text-yellow-800 text-sm">
                              <i className="fas fa-info-circle mr-2"></i>
                              However, no method of transmission over the internet is 100% secure.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Your Rights */}
                  <section id="your-rights" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#393E46' }}>
                        <i className="fas fa-user-shield text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Your Rights
                      </h2>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-6">
                      You have the following rights regarding your personal data:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      {[
                        { icon: 'fas fa-edit', text: 'Access and update your personal information' },
                        { icon: 'fas fa-trash-alt', text: 'Delete your account and associated data' },
                        { icon: 'fas fa-envelope-open-text', text: 'Opt out of marketing communications' },
                        { icon: 'fas fa-download', text: 'Request a copy of your data' },
                        { icon: 'fas fa-ban', text: 'Restrict or object to certain data processing' }
                      ].map((item, index) => (
                        <div key={index} className="flex items-center p-4 bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow">
                          <i className={`${item.icon} text-xl mr-4`} style={{ color: '#948979' }}></i>
                          <span className="text-gray-700">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* Cookies */}
                  <section id="cookies" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#948979' }}>
                        <i className="fas fa-cookie-bite text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Cookies and Tracking
                      </h2>
                    </div>
                    <div className="bg-orange-50 border-l-4 border-orange-400 p-6 rounded-r-xl">
                      <p className="text-gray-700 leading-relaxed">
                        We use cookies and similar technologies to enhance your experience, remember your preferences,
                        and analyze platform usage. You can control cookie settings through your browser preferences.
                      </p>
                    </div>
                  </section>

                  {/* Advertising */}
                  <section id="advertising" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#393E46' }}>
                        <i className="fas fa-ad text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Advertising
                      </h2>
                    </div>
                    <div className="space-y-6">
                      <p className="text-gray-700 leading-relaxed">
                        We may use third-party advertising services such as Google AdSense to display ads.
                      </p>
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-xl">
                        <p className="text-gray-700 leading-relaxed mb-4">
                          These advertising partners may use cookies and similar technologies to:
                        </p>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <i className="fas fa-bullseye text-blue-500 mt-1 mr-3 text-sm"></i>
                            <span className="text-gray-700">Show personalized ads</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-chart-line text-blue-500 mt-1 mr-3 text-sm"></i>
                            <span className="text-gray-700">Measure ad performance</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-star text-blue-500 mt-1 mr-3 text-sm"></i>
                            <span className="text-gray-700">Improve ad quality</span>
                          </li>
                        </ul>
                      </div>
                      <div className="bg-gray-50 p-6 rounded-xl">
                        <p className="text-gray-700 leading-relaxed">
                          Google and other vendors may use cookies based on your previous visits to this website or other websites.
                        </p>
                      </div>
                      <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-r-xl">
                        <p className="text-gray-700 leading-relaxed">
                          Users may manage or withdraw consent through our cookie consent banner (for users in the EEA, UK, and Switzerland).
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Third-Party Links */}
                  <section id="third-party-links" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#948979' }}>
                        <i className="fas fa-external-link-alt text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Third-Party Links
                      </h2>
                    </div>
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-xl">
                      <p className="text-gray-700 leading-relaxed">
                        Our website may contain links to external websites. We are not responsible for the privacy practices of those websites.
                      </p>
                    </div>
                  </section>

                  {/* Children's Privacy */}
                  <section id="children-privacy" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#393E46' }}>
                        <i className="fas fa-child text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Children's Privacy
                      </h2>
                    </div>
                    <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-xl">
                      <p className="text-gray-700 leading-relaxed">
                        Our service is not intended for children under 18. We do not knowingly collect personal
                        information from children under 18. If you become aware that a child has provided us with
                        personal information, please contact us immediately.
                      </p>
                    </div>
                  </section>

                  {/* Updates */}
                  <section id="policy-updates" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#948979' }}>
                        <i className="fas fa-sync-alt text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Policy Updates
                      </h2>
                    </div>
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-xl">
                      <p className="text-gray-700 leading-relaxed">
                        We may update this privacy policy from time to time. We will notify you of any changes by
                        posting the new policy on this page and updating the "Last updated" date. We encourage you
                        to review this policy periodically.
                      </p>
                    </div>
                  </section>

                  {/* Contact */}
                  <section id="contact" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#393E46' }}>
                        <i className="fas fa-envelope text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Contact Us
                      </h2>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-6">
                      If you have any questions about this Privacy Policy or our data practices, please contact us:
                    </p>
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-2xl border shadow-lg">
                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="text-center">
                          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
                            <i className="fas fa-envelope text-white text-xl"></i>
                          </div>
                          <h4 className="font-semibold mb-2" style={{ color: '#222831' }}>Email</h4>
                          <p className="text-gray-600">info.vestrioz@gmail.com</p>
                        </div>
                        <div className="text-center">
                          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#393E46' }}>
                            <i className="fas fa-users text-white text-xl"></i>
                          </div>
                          <h4 className="font-semibold mb-2" style={{ color: '#222831' }}>Team</h4>
                          <p className="text-gray-600">VestrioZ Privacy Team</p>
                        </div>
                        <div className="text-center">
                          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
                            <i className="fas fa-clock text-white text-xl"></i>
                          </div>
                          <h4 className="font-semibold mb-2" style={{ color: '#222831' }}>Response Time</h4>
                          <p className="text-gray-600">Within 48 hours</p>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Navigation */}
          <div className="mt-12 text-center">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#222831' }}>
                Ready to get started?
              </h3>
              <p className="text-gray-600 mb-6">
                Join thousands of fashion enthusiasts sharing and renting amazing pieces.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                  style={{ backgroundColor: '#222831', color: 'white' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#222831'}
                >
                  <i className="fas fa-home mr-2"></i>
                  Return to VestrioZ
                </Link>
                <Link
                  to="/terms-of-service"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold transition-all duration-300 border-2 hover:shadow-lg hover:-translate-y-1"
                  style={{ borderColor: '#948979', color: '#948979' }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#948979';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#948979';
                  }}
                >
                  <i className="fas fa-file-contract mr-2"></i>
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;
